import React from 'react';
import i18n from 'i18n-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { SmartAssistMarkdown } from './SmartAssistMarkdown';
import {
  getConfidencePercent,
  getTipItems,
  parseSmartAssistResponse,
  plainTextFromMarkdown,
  smartAssistStructuredMarkers,
} from './smartAssistParsing';

export interface SmartAssistStructuredResponseProps {
  text: string;
  streaming: boolean;
}

export function SmartAssistStructuredResponse({
  text,
  streaming,
}: SmartAssistStructuredResponseProps): React.ReactElement | null {
  const parts = parseSmartAssistResponse(text);
  if (parts.length === 0) return null;

  return (
    <div className="smartAssist__structured-response">
      {parts.map((part, idx) => {
        if (part.type === 'markdown') {
          return <SmartAssistMarkdown key={`${part.type}-${idx}`}>{part.content}</SmartAssistMarkdown>;
        }

        if (part.type === 'confidence') {
          const percent = getConfidencePercent(part.content);
          return (
            <div key={`${part.type}-${idx}`} className="smartAssist__confidence">
              <div className="smartAssist__confidence-label">
                <FontAwesomeIcon icon={['fas', 'chart-line']} />
                <span>{i18n.translate('smartAssist.structured.confidenceThreshold')}</span>
              </div>
              {percent == null ? (
                <span className="smartAssist__confidence-badge">{plainTextFromMarkdown(part.content)}</span>
              ) : (
                <div
                  className="smartAssist__confidence-progress"
                  aria-label={
                    i18n.translate('smartAssist.structured.confidenceThresholdPercentAria', { percent }) as string
                  }
                >
                  <span style={{ width: `${percent}%` }} />
                </div>
              )}
              {percent != null && <span className="smartAssist__confidence-badge">{`${percent}%`}</span>}
            </div>
          );
        }

        if (part.type === 'tailor') {
          return (
            <details
              key={`${part.type}-${idx}`}
              className={clsx('smartAssist__semantic-section', 'smartAssist__semantic-section--tailor')}
              open
            >
              <summary>
                <span className="smartAssist__semantic-heading">
                  <FontAwesomeIcon icon={smartAssistStructuredMarkers.tailor.icon} />
                  {i18n.translate(smartAssistStructuredMarkers.tailor.labelKey)}
                </span>
                <FontAwesomeIcon icon={['fas', 'chevron-down']} className="smartAssist__semantic-toggle-icon" />
              </summary>
              <div className="smartAssist__semantic-content">
                <SmartAssistMarkdown>{part.content}</SmartAssistMarkdown>
              </div>
            </details>
          );
        }

        if (part.type === 'tips') {
          const tips = getTipItems(part.content);
          return (
            <section
              key={`${part.type}-${idx}`}
              className={clsx('smartAssist__semantic-section', 'smartAssist__semantic-section--tips')}
            >
              <div className="smartAssist__semantic-heading">
                <FontAwesomeIcon icon={smartAssistStructuredMarkers.tips.icon} />
                {i18n.translate(smartAssistStructuredMarkers.tips.labelKey)}
              </div>
              <div className="smartAssist__tip-chips">
                {tips.map((tip, tipIdx) => (
                  <span key={`${tip}-${tipIdx}`} className="smartAssist__tip-chip">
                    {tip}
                  </span>
                ))}
              </div>
            </section>
          );
        }

        const marker = smartAssistStructuredMarkers[part.type as keyof typeof smartAssistStructuredMarkers];
        return (
          <section
            key={`${part.type}-${idx}`}
            className={clsx('smartAssist__semantic-section', `smartAssist__semantic-section--${part.type}`)}
          >
            <div className="smartAssist__semantic-heading">
              <FontAwesomeIcon icon={marker.icon} />
              {i18n.translate(marker.labelKey)}
            </div>
            <div className="smartAssist__semantic-content">
              <SmartAssistMarkdown>{part.content}</SmartAssistMarkdown>
            </div>
          </section>
        );
      })}
      {streaming && <span className="smartAssist__msg-stream-cursor" aria-hidden="true" />}
    </div>
  );
}
